import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { MagnifyingGlass, ArrowUpRight, ChatCircleText } from "@phosphor-icons/react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatDate, formatCurrency, formatDateTime } from "@/lib/format";
import { useCategories } from "@/hooks/useCategories";

const STATUS_META = {
  PENDING_REVIEW: { label: "Pending", cls: "bg-zinc-100 text-zinc-800" },
  IN_REVIEW: { label: "In review", cls: "bg-amber-50 text-amber-800" },
  QUOTED: { label: "Quoted", cls: "bg-[#FF5A1F]/10 text-[#E04814]" },
  APPROVED: { label: "Approved", cls: "bg-green-50 text-green-800" },
  REJECTED: { label: "Rejected", cls: "bg-red-50 text-red-800" },
};

const STATUS_OPTIONS = [
  { key: "all", label: "All statuses" },
  { key: "PENDING_REVIEW", label: "Pending review" },
  { key: "QUOTED", label: "Quoted" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
];

export default function AdminQuotations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { labelFor } = useCategories();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/quotations", { params });
      setItems(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filtered = items.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (r.quotation_number || "").toLowerCase().includes(s) ||
      (r.material || "").toLowerCase().includes(s) ||
      (r.client_name || "").toLowerCase().includes(s) ||
      (r.client_email || "").toLowerCase().includes(s) ||
      (r.project_name || "").toLowerCase().includes(s)
    );
  });

  const pending = items.filter((r) => r.status === "PENDING_REVIEW").length;

  return (
    <Layout>
      <div className="mb-8">
        <div className="mt-tiny text-[#FF5A1F]">Factory Console · Quotations</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-zinc-900 mt-2">
          Client quote requests
        </h1>
        <p className="mt-2 text-zinc-600 text-sm">
          {pending > 0 ? <><b className="text-[#FF5A1F]">{pending}</b> awaiting your quote.</> : "All caught up — no pending requests."}
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="admin-quotes-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search #, material, client..."
            className="w-full pl-10 pr-4 py-3 bg-white border border-zinc-200 focus:border-zinc-900 outline-none transition-colors"
          />
        </div>
        <div className="w-full md:w-64">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="admin-quotes-status-filter" className="h-12 border-zinc-200 rounded-none bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-zinc-200" data-testid="admin-quotes-table">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-200">
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Quote #</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Client</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Material</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Status</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt">Submitted</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt text-right">Amount</TableHead>
              <TableHead className="mt-tiny text-zinc-500 font-mono-mt text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              ["s-1","s-2","s-3"].map((k) => (
                <TableRow key={k}><TableCell colSpan={7}><div className="h-6 bg-zinc-100 animate-pulse" /></TableCell></TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-500">
                <ChatCircleText size={40} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
                No quote requests {q || statusFilter !== "all" ? "match your filters" : "yet"}.
              </TableCell></TableRow>
            ) : filtered.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.PENDING_REVIEW;
              return (
                <TableRow key={r.quotation_id} className="hover:bg-zinc-50 border-zinc-200">
                  <TableCell className="font-mono-mt text-sm">{r.quotation_number}</TableCell>
                  <TableCell>
                    <div className="font-medium text-zinc-900">{r.client_name}</div>
                    <div className="mt-tiny text-zinc-400 font-mono-mt">{r.client_email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-zinc-900">{r.material}</div>
                    <div className="mt-tiny text-zinc-400">{labelFor(r.category)}{r.quantity ? ` · Qty ${r.quantity}` : ""}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.15em] ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{formatDate(r.created_at)}</TableCell>
                  <TableCell className="font-mono-mt font-semibold text-right">{r.quote_amount != null ? formatCurrency(r.quote_amount) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Link
                      to={`/admin/quotations/${r.quotation_id}`}
                      data-testid={`admin-quote-open-${r.quotation_number}`}
                      className="inline-flex items-center gap-1.5 border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white px-3 py-1.5 text-xs font-medium transition-colors"
                    >
                      {r.status === "PENDING_REVIEW" ? "Send quote" : "Open"} <ArrowUpRight size={12} />
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Layout>
  );
}
